import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from "@angular/core";
import { FiatService } from "@main-services/currency/fiat.service";
import { TradeComponent } from "../trade.interface";
import { FiatWallet } from "@models";
import { UserService } from "@main-services/user.service";
import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { ModalHeader } from "@models/modal_step";
import { TradeOfferService } from "../../services/trade-offer.service";
import { round } from "@helpers/utils";
import { toEuroByFiatId } from "@helpers/tradable-convert";
import { SELECTED_CURRENCY, TradeOfferStatus } from "@models/trade-offer.model";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { AccountService } from "../../../account/services/account.service";
import { TradePendingComponent } from "../../../main/components/step-summary/success-screens/trade-pending/trade-pending.component";
import { ApicallService } from "@core-services/apicall.service";
import { InfoService } from "@core-services/info.service";
import { ConfirmModalService } from "@core-services/confirm-modal.service";
import { UntilDestroy } from "@ngneat/until-destroy";
import { UserFiatFundsService } from "@modules/shared/services/user-fiat-funds.service";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { StockTestService } from "@main-services/stock-test.service";
import { StockTestHelperService } from "@main-services/stock-test-helper.service";
import { Tradable } from "@models/interfaces/tradable";
import { LeveragedTokensFacade } from "@modules/main/components/leveraged-tokens/state/leveraged-tokens.facade";
import { combineLatest, Observable } from "rxjs";
import { ICashPlusFiatWallet } from "@modules/cashplus/entities/cashplus.model";
import { CashplusTradingFacade } from "../../services/cashplus-trading-facade.service";
import { take } from "rxjs/operators";
import { Router } from "@angular/router";
import { OrdersEntryService } from "@modules/limit-orders/services/orders-entry.service";
import { LimitOrderQueryParamName } from "@modules/limit-orders/interfaces/limit-orders-route.interface";

@UntilDestroy()
@Component({
	templateUrl: "./trade-buy-modal.component.html",
	styleUrls: ["../trade-modal.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [CashplusTradingFacade],
	standalone: false
})
export class TradeBuyComponent extends TradeComponent implements OnInit, OnDestroy {
	private cashplusTradingFacade = inject(CashplusTradingFacade);
	private router = inject(Router);

	protected label: string = "buy";

	// Some view keys:
	protected readonly flowName: string = "Buy";
	protected readonly pathPrepend: string = "/trade/buy";
	public subtitle: string = "";

	public hasFiatFunds: boolean = this.userFiatFundsService.hasFiatWalletsWithFunds();
	public wallets: TradableWallet[] = [];

	// TODO: CFD CLEANUP, remove flag
	public cfdEnabled$: Observable<boolean> = this.launchDarklyService.isActive$(LD_FLAG.LEVERAGE_CFD);

	// Cashplus
	public cashPlusFiatTilesVM$: Observable<ICashPlusFiatWallet[]>;
	public cashPlusFF$: Observable<boolean> = this.cashplusTradingFacade.cashPlusBuyEnabled$;
	private hasEligibleCashPlusWallets: boolean = false;

	public isLimitOrderEnabled: boolean = false;

	private ordersEntryService = inject(OrdersEntryService);

	public constructor(
		protected apicallService: ApicallService,
		protected walletService: TradableWalletListService,
		protected accountService: AccountService,
		protected fiatService: FiatService,
		protected tradableListService: TradableListService,
		protected fiatwalletService: FiatWalletService,
		protected infoService: InfoService,
		protected userService: UserService,
		protected tradeOfferService: TradeOfferService,
		protected launchDarklyService: LaunchDarklyService,
		protected confirmModalService: ConfirmModalService,
		protected stockTestService: StockTestService,
		protected stockTestHelperService: StockTestHelperService,
		protected userFiatFundsService: UserFiatFundsService,
		protected leveragedTokenFacade: LeveragedTokensFacade
	) {
		super(
			apicallService,
			fiatwalletService,
			infoService,
			tradableListService,
			fiatService,
			accountService,
			walletService,
			userService,
			tradeOfferService,
			launchDarklyService,
			confirmModalService,
			leveragedTokenFacade
		);
	}

	public ngOnInit(): void {
		this.showTradeFlow$.next(!this.data.isDirectBestOffer());
		// limit order
		combineLatest({
			cashplus: this.cashPlusFacade.getConfigurationsState$(),
			buyOrderFlag: this.launchDarklyService.isActive$(LD_FLAG.LIMIT_ORDERS_BUY)
		})
			.pipe(take(1))
			.subscribe(({ cashplus, buyOrderFlag }) => {
				if (buyOrderFlag) {
					this.isLimitOrderEnabled = true;
				}
				const nonEmptyWallets = this.getWalletForCashPlus().filter((fiatWallet) => fiatWallet.getBalance() > 0);
				this.hasEligibleCashPlusWallets = nonEmptyWallets.some(
					(fiatWallet) =>
						fiatWallet.getFiatId() ===
						cashplus.configurations.find((c) => c.fiat_id === fiatWallet.getFiatId())?.fiat_id
				);
			});
		this.cashPlusFiatTilesVM$ = this.cashplusTradingFacade.buyFiatTilesVM$(this.getWalletForCashPlus());

		super.ngOnInit();
	}

	private getWalletForCashPlus(): FiatWallet[] {
		return this.fiatwalletService.list;
	}

	public onTradableSelect(tradable: Tradable, cashPlusFF: boolean): void {
		const wallets = this.walletService.getWalletsByTradableId(tradable.getId());

		if (wallets.length === 1) {
			// if 1 wallet, set it to null to not show multiple wallets screen
			// If entered from limit order close buy and open orders flow
			if (this.isLimitOrder) {
				this.ordersEntryService.openBuyOrderFlow(tradable.getId());
				this.close();
			}
			this.tradableId = null;
			this.onWalletSelect(wallets[0], cashPlusFF);
		} else {
			// if multiple wallets, go to wallet selection screen
			this.wallets = wallets;
			this.tradableId = tradable.getId();
		}
	}

	public onWalletSelect(wallet: TradableWallet, cashPlusFF: boolean): void {
		const callback: Function = () => {
			// if CashPlus FF is on, we need to provide also the CashPlus wallets to other parts of the flow
			if (cashPlusFF && this.hasEligibleCashPlusWallets) {
				this.cashPlusFiatTilesVM$.pipe(take(1)).subscribe({
					next: (vm) => {
						this.selectWallet(wallet, vm);

						if (vm.length === 1 && vm[0].isEligible) {
							this.selectedCashPlusWallet = vm[0];
						} else {
							this.selectedCashPlusWallet = vm.filter(
								(d) => d.id === this.fiatWallet?.getFiatId() && d.isEligible
							)[0];
						}
					}
				});
			} else {
				this.selectWallet(wallet);
			}
		};

		if (this.stockTestService.needsTest(wallet.getCurrencyId())) {
			this.stockTestHelperService.startTest(callback);
		} else {
			callback();
		}
	}

	public getFiatWallets(cashPlusWallets?: ICashPlusFiatWallet[]): FiatWallet[] {
		if (cashPlusWallets) {
			return cashPlusWallets.map((cpWallets) => cpWallets.fiatWallet);
		}

		const min: number = round(this.getTradable().getMinBuy(), this.getFiat().getPrecision());
		return this.walletsWithFunds(min);
	}

	// select cash plus fiat wallet
	public selectCashPlusFiatWallet(wallet: ICashPlusFiatWallet): void {
		this.selectFiatWallet(wallet.fiatWallet);
		if (wallet.isEligible) {
			this.selectedCashPlusWallet = wallet;
		}
	}

	public selectWallet(wallet: TradableWallet, cashPlusWallets?: ICashPlusFiatWallet[]): void {
		if (this.isLimitOrder) {
			this.ordersEntryService.openBuyOrderFlow(wallet.getCurrencyId(), {
				[LimitOrderQueryParamName.WalletId]: wallet.getId()
			});
			this.close();
		}
		super.selectWallet(wallet, cashPlusWallets);
	}

	public walletsWithFunds(min: number): FiatWallet[] {
		return this.fiatwalletService.list.filter((f) => {
			const fiat = this.fiatService.getItemById(f.getFiatId());
			if (!fiat) {
				return false;
			}

			return toEuroByFiatId(f.getBalance(), this.fiatService.getItemById(f.getFiatId())) >= min;
		});
	}

	public stepBack(): void {
		// Reset the default wallet for calculating the min when going back to Fiat Wallets selection. Otherwise, the min is being set from previous selected wallet (/limits).
		if (!this.edit && this.isThirdStep) {
			this.setDefaultFiatId();
		}
		super.stepBack();
	}

	public showSuccessScreen(tradeStatus: TradeOfferStatus): void {
		this.done();
		if (tradeStatus === "SUCCESS") {
			this.showTradeSuccessScreen({
				offer: this.data,
				itbSuccess: this.getItbTradeSuccess(),
				previousBalance: this.preTransBalance
			});
		} else {
			const walletExists = !this.wallet.isNew();
			this.modalService
				.create(TradePendingComponent, {
					offer: this.data,
					walletExists,
					itbSuccess: this.getItbTradeSuccess(),
					hasItb: this.hasItb(),
					previousBalance: this.preTransBalance
				})
				.present();
		}
	}

	public get currentStep(): ModalHeader {
		const header: ModalHeader = this.getDefaultHeader(this.getTitle());

		if (
			(this.isFirstStep && this.tradableId) ||
			(this.isSecondStep && this.isEdit(0) && !this.data.getDirectTradableWalletOffer()) ||
			(this.isThirdStep && !this.isEdit(3))
		) {
			header.left = "back";
			this.subtitle = this.getSubtitle();
		} else if ((this.isFirstStep && this.isEdit(1)) || (this.isSecondStep && this.isEdit(2))) {
			header.left = "abort";
		}

		if ((this.isFirstStep && this.isEdit(1)) || (this.isSecondStep && this.isEdit(2))) {
			header.right = "ok";
		} else if (
			(this.isFirstStep && !this.isEdit(1)) ||
			(this.isSecondStep && !this.isEdit(2)) ||
			this.isThirdStep
		) {
			header.right = "abort";
		}

		return header;
	}

	public get userWantsCrypto(): boolean {
		return this.getSelectedCurrency() === SELECTED_CURRENCY.CRYPTOCOIN;
	}

	public onHeaderButtonClick(name: string): void {
		// Can probably be unified for all flows in the future
		if (name === "back") {
			if (this.isFirstStep) {
				this.resetTradable();
			}
			if (this.isSecondStep) {
				this.tradableId = null;
			}
			if (this.isThirdStep) {
				this.priceSubject$.next(null);
			}

			this.stepBack();
		} else {
			super.onHeaderButtonClick(name);
		}
	}

	public getTitle(): string {
		const tradable = this.getTradable();
		switch (this.stepNumber) {
			case 1:
				return this.translate("buy_cryptocoins", [tradable.getSymbol()]);
			case 2:
				return this.translate("trade_fiat_select_header", [tradable.getName()]);
			case 3:
				if (this.isLimitOrderEnabled) {
					return this.translate("buy_market_order");
				}
				return tradable.isToken()
					? this.translate("fma_invest", [tradable.getName()])
					: this.translate("trade_amount_select_header");
			default:
				return "";
		}
	}

	public goToFiatWallet(): void {
		this.close();
		this.router.navigateByUrl(`/fiat-wallet/${this.fiatWallet.getId()}`);
	}

	public modalCanDismiss(): boolean {
		return true;
	}

	private getSubtitle(): string {
		const tradable = this.getTradable();
		if (this.getTradable().isToken()) {
			return this.translate("fma_select_amount");
		}
		return this.translate("buy_amount_of_crypto", [this.getFiat().getName(), tradable.getName()]);
	}

	public ngOnDestroy(): void {
		super.ngOnDestroy();
	}
}
